/* eslint-disable no-param-reassign */
/**
 * Upload Progress slice - manages upload state for background video uploads
 * Fully Redux-based implementation with async thunks
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import SectionService from '../../../../services/SectionService';
import UploadService from '../../../../services/UploadService';
import { getAssetFile, removeAssetFile } from '../../lib/assetFileStorage';

const MAX_CONCURRENT_UPLOADS = 3;
const MAX_RETRY_ATTEMPTS = 3;

// Helper to get active upload count
const getActiveUploadCount = (uploads) => {
  return Object.values(uploads).filter(
    (u) => u.status === 'uploading'
  ).length;
};

// Helper to get next queued upload
const getNextQueuedUpload = (uploads, uploadQueue) => {
  const queuedEntries = Object.entries(uploadQueue);
  if (queuedEntries.length === 0) return null;
  
  // Find first queued upload that has meetingId
  const found = queuedEntries.find(([assetId, queueData]) => 
    queueData.meetingId && uploads[assetId]?.status === 'queued'
  );
  return found ? { assetId: found[0], ...found[1] } : null;
};

const initialState = {
  uploads: {}, // assetId -> upload state
  uploadQueue: {}, // assetId -> { file reference stored separately, meetingId }
  retryCounts: {}, // assetId -> retry count
};

/**
 * Thunk to process queued uploads
 */
export const processQueue = createAsyncThunk(
  'uploadProgress/processQueue',
  async (_, { dispatch, getState }) => {
    const state = getState().uploadProgress;
    const activeCount = getActiveUploadCount(state.uploads);
    
    if (activeCount >= MAX_CONCURRENT_UPLOADS) {
      return; // Can't process more
    }

    const next = getNextQueuedUpload(state.uploads, state.uploadQueue);
    if (!next) {
      return; // No queued uploads ready
    }

    const { assetId, meetingId } = next;
    
    // Get file from storage
    const file = getAssetFile(assetId);
    if (!file) {
      return; // File not found, skip
    }
    
    // Remove from queue (action defined in slice below)
    dispatch({ type: 'uploadProgress/removeFromQueue', payload: { assetId } });

    // Start upload via the thunk so middleware actually runs the upload.
    // Forward-reference is safe: startUpload is defined later in this module
    // but is resolved at runtime when this thunk executes.
    // eslint-disable-next-line no-use-before-define
    dispatch(startUpload({ assetId, file, meetingId }));
  }
);

/**
 * Async thunk to retry a failed upload
 */
export const retryUpload = createAsyncThunk(
  'uploadProgress/retryUpload',
  async ({ assetId }, { dispatch, getState, rejectWithValue }) => {
    const state = getState().uploadProgress;
    const uploadState = state.uploads[assetId];
    
    if (!uploadState || uploadState.status !== 'failed') {
      return rejectWithValue({ message: 'Upload is not in failed state' });
    }

    // Get file from storage
    const file = getAssetFile(assetId);
    if (!file) {
      return rejectWithValue({ message: 'File not found for retry' });
    }

    // Get meetingId from upload state or queue
    const meetingId = uploadState.meetingId || state.uploadQueue[assetId]?.meetingId;
    if (!meetingId) {
      return rejectWithValue({ message: 'Meeting ID not found' });
    }

    // Increment retry count (action defined in slice below)
    dispatch({ type: 'uploadProgress/incrementRetryCount', payload: { assetId } });

    // Start upload via the thunk so middleware actually runs the upload.
    try {
      // eslint-disable-next-line no-use-before-define
      const result = await dispatch(startUpload({ assetId, file, meetingId })).unwrap();
      return result;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Async thunk to perform the actual upload
 */
const performUpload = createAsyncThunk(
  'uploadProgress/performUpload',
  async ({ assetId, file, meetingId }, { dispatch, rejectWithValue }) => {
    try {
      // 1. Create placeholder section
      const placeholderResponse = await SectionService.createPlaceholderSection({
        meetingId,
        title: file.name,
        type: file.type.includes('video') ? 'video' : 'audio',
      });

      const sectionId = placeholderResponse.id;

      // Update state with sectionId (action defined in slice below)
      dispatch({ type: 'uploadProgress/setSectionId', payload: { assetId, sectionId } });

      // 2. Upload file to S3 with progress tracking
      const uploadProgressCallback = (progress) => {
        // Update progress (action defined in slice below)
        dispatch({ type: 'uploadProgress/updateUploadProgress', payload: { assetId, progress: Math.round(progress) } });
      };

      let s3FileKey;
      const fileType = file.type.includes('video') ? 'video' : 'audio';

      if (file.size < 5 * 1024 * 1024) {
        // Single-part upload
        s3FileKey = await UploadService.uploadVideoToS3(file, uploadProgressCallback);
      } else {
        // Multipart upload
        s3FileKey = await UploadService.uploadVideoToS3InParts(file, uploadProgressCallback);
      }

      // Extract duration from file (simplified - would need actual video processing)
      // For now, set a default duration
      const duration = 60; // Default 1 minute

      // 3. Complete the section
      await SectionService.completeSection({
        sectionId,
        s3FileKey,
        duration,
        fileType,
      });

      return { assetId, sectionId };
    } catch (error) {
      // Extract error message from axios error response - preserve server message
      const errorMessage = error?.response?.data?.message || error?.message || 'Upload failed';
      
      // Reject with the proper error message so it can be caught by startUpload
      return rejectWithValue({ 
        message: errorMessage,
        response: error?.response,
        status: error?.response?.status,
      });
    }
  }
);

/**
 * Async thunk to start an upload
 */
export const startUpload = createAsyncThunk(
  'uploadProgress/startUpload',
  async ({ assetId, file, meetingId }, { dispatch, getState, rejectWithValue }) => {
    const state = getState().uploadProgress;
    
    // Check if already uploading or completed
    const existing = state.uploads[assetId];
    if (existing && (existing.status === 'uploading' || existing.status === 'completed')) {
      return { assetId, sectionId: existing.sectionId };
    }

    // Check concurrency limit
    const activeCount = getActiveUploadCount(state.uploads);
    if (activeCount >= MAX_CONCURRENT_UPLOADS) {
      // Add to queue (action defined in slice below)
      dispatch({ type: 'uploadProgress/queueUpload', payload: { assetId, file, meetingId } });
      return rejectWithValue({ queued: true });
    }

    // Start upload immediately
    try {
      // Set uploading state (action defined in slice below)
      dispatch({ type: 'uploadProgress/setUploading', payload: { assetId, meetingId } });
      const result = await dispatch(performUpload({ assetId, file, meetingId })).unwrap();
      
      // Mark as completed (action defined in slice below)
      dispatch({ type: 'uploadProgress/completeUpload', payload: { assetId, sectionId: result.sectionId } });
      
      // Update timeline item metadata with sectionId
      const timelineState = getState().timeline;
      const timelineItem = timelineState.items.find((item) => item.asset_id === assetId);
      if (timelineItem) {
        dispatch({ 
          type: 'timeline/updateItemMetadata', 
          payload: { 
            itemId: timelineItem.id, 
            metadata: { sectionId: result.sectionId } 
          } 
        });
      }
      
    // Clean up file (file is stored separately, safe to remove)
    removeAssetFile(assetId);
      
      // Process next queued upload
      dispatch(processQueue());
      
      return result;
    } catch (error) {
      // Extract error message - check rejectWithValue payload first, then axios error response
      let errorMessage = 'Upload failed';
      let errorStatus = null;
      
      if (error?.payload) {
        // Error from performUpload rejectWithValue
        errorMessage = error.payload.message || errorMessage;
        errorStatus = error.payload.status;
      } else if (error?.response?.data?.message) {
        // Direct axios error response
        errorMessage = error.response.data.message;
        errorStatus = error.response.status;
      } else if (error?.message) {
        // Generic error message
        errorMessage = error.message;
      }
      
      // Check if this is a non-retryable error (quota, validation, etc.)
      const isNonRetryableError = errorStatus ? (errorStatus >= 400 && errorStatus < 500) : false;
      
      // Mark as failed (action defined in slice below)
      dispatch({ type: 'uploadProgress/failUpload', payload: { assetId, error: errorMessage } });
      
      // Only auto-retry for retryable errors (network, 5xx, etc.)
      if (!isNonRetryableError) {
        const retryCount = state.retryCounts[assetId] || 0;
        if (retryCount < MAX_RETRY_ATTEMPTS) {
          const delay = (2 ** retryCount) * 1000; // 1s, 2s, 4s
          setTimeout(() => {
            // Retry upload
            dispatch(retryUpload({ assetId }));
          }, delay);
        }
      }
      
      // Process next queued upload even on failure
      dispatch(processQueue());
      
      // Return error with message for toast notification
      return rejectWithValue({ message: errorMessage, isNonRetryable: isNonRetryableError });
    }
  }
);


/**
 * Thunk to process queued uploads when meetingId becomes available
 * Note: updateQueueEntry action is defined in the slice below
 */
export const processQueuedUploads = createAsyncThunk(
  'uploadProgress/processQueuedUploads',
  async (meetingId, { dispatch, getState }) => {
    const state = getState().uploadProgress;
    const queuedEntries = Object.entries(state.uploadQueue);
    
    // Iterate queued entries to kick off uploads once meetingId is available
    queuedEntries.forEach(([assetId, queueData]) => {
      if (!queueData.meetingId) {
        // Update queue entry with meetingId
        dispatch({ type: 'uploadProgress/updateQueueEntry', payload: { assetId, meetingId } });
        
        // Start upload using the thunk (not a plain action)
        const file = getAssetFile(assetId);
        if (file) {
          // Dispatch the startUpload thunk directly
          dispatch(startUpload({ assetId, file, meetingId }));
        }
      }
    });
  }
);

const uploadProgressSlice = createSlice({
  name: 'uploadProgress',
  initialState,
  reducers: {
    // Set upload state to uploading
    setUploading: (state, action) => {
      const { assetId } = action.payload;
      state.uploads[assetId] = {
        assetId,
        status: 'uploading',
        progress: 0,
        sectionId: null,
        error: null,
        startedAt: Date.now(),
        completedAt: null,
        meetingId: action.payload.meetingId,
      };
    },

    // Set section ID for upload
    setSectionId: (state, action) => {
      const { assetId, sectionId } = action.payload;
      if (state.uploads[assetId]) {
        state.uploads[assetId].sectionId = sectionId;
      }
    },

    // Update progress for specific upload
    updateUploadProgress: (state, action) => {
      const { assetId, progress } = action.payload;
      if (state.uploads[assetId]) {
        state.uploads[assetId].progress = progress;
      }
    },

    // Mark upload as completed
    completeUpload: (state, action) => {
      const { assetId, sectionId } = action.payload;
      if (state.uploads[assetId]) {
        state.uploads[assetId].status = 'completed';
        state.uploads[assetId].progress = 100;
        state.uploads[assetId].sectionId = sectionId;
        state.uploads[assetId].completedAt = Date.now();
      }
    },

    // Mark upload as failed
    failUpload: (state, action) => {
      const { assetId, error } = action.payload;
      if (state.uploads[assetId]) {
        state.uploads[assetId].status = 'failed';
        state.uploads[assetId].error = error;
        state.uploads[assetId].progress = 0;
      }
    },

    // Queue upload for when meetingId becomes available
    queueUpload: (state, action) => {
      const { assetId, meetingId } = action.payload;
      state.uploadQueue[assetId] = { meetingId: meetingId || null };
      state.uploads[assetId] = {
        assetId,
        status: 'queued',
        progress: 0,
        sectionId: null,
        error: null,
        startedAt: Date.now(),
        completedAt: null,
        meetingId: meetingId || null,
      };
    },

    // Update queue entry with meetingId
    updateQueueEntry: (state, action) => {
      const { assetId, meetingId } = action.payload;
      if (state.uploadQueue[assetId]) {
        state.uploadQueue[assetId].meetingId = meetingId;
        if (state.uploads[assetId]) {
          state.uploads[assetId].meetingId = meetingId;
        }
      }
    },

    // Remove from queue
    removeFromQueue: (state, action) => {
      const { assetId } = action.payload;
      delete state.uploadQueue[assetId];
    },

    // Cancel upload
    cancelUpload: (state, action) => {
      const { assetId } = action.payload;
      const uploadState = state.uploads[assetId];
      
      if (!uploadState) return;

      // Remove from queue if queued
      delete state.uploadQueue[assetId];

      // If uploading, mark as cancelled
      if (uploadState.status === 'uploading') {
        uploadState.status = 'failed';
        uploadState.error = 'Upload cancelled';
      }

      // Remove from uploads
      delete state.uploads[assetId];
    },

    // Remove upload state
    removeUpload: (state, action) => {
      const { assetId } = action.payload;
      delete state.uploads[assetId];
      delete state.uploadQueue[assetId];
      delete state.retryCounts[assetId];
    },

    // Increment retry count
    incrementRetryCount: (state, action) => {
      const { assetId } = action.payload;
      state.retryCounts[assetId] = (state.retryCounts[assetId] || 0) + 1;
    },

    // Clear all uploads
    clearAllUploads: (state) => {
      state.uploads = {};
      state.uploadQueue = {};
      state.retryCounts = {};
    },
  },
});

export const {
  setUploading,
  setSectionId,
  updateUploadProgress,
  completeUpload,
  failUpload,
  queueUpload,
  updateQueueEntry,
  removeFromQueue,
  cancelUpload,
  removeUpload,
  incrementRetryCount,
  clearAllUploads,
} = uploadProgressSlice.actions;

// Selectors
export const selectUploadState = (assetId) => (state) => {
  return state.uploadProgress.uploads[assetId] || null;
};

export const selectAllUploadStates = (state) => {
  return state.uploadProgress.uploads;
};

export const selectUploadStatus = (state) => {
  const uploads = Object.values(state.uploadProgress.uploads);
  const uploadingCount = uploads.filter((u) => u.status === 'uploading').length;
  const failedCount = uploads.filter((u) => u.status === 'failed').length;
  const completedCount = uploads.filter((u) => u.status === 'completed').length;
  const queuedCount = uploads.filter((u) => u.status === 'queued').length;

  return {
    hasActiveUploads: uploadingCount > 0 || queuedCount > 0,
    uploadingCount,
    failedCount,
    completedCount,
    queuedCount,
    totalCount: uploads.length,
    allCompleted: uploads.length > 0 && uploads.every((u) => u.status === 'completed'),
  };
};

export default uploadProgressSlice.reducer;
